import {isPlatformServer} from '@angular/common';
import {inject, PLATFORM_ID} from '@angular/core';
import {type ResolveFn} from '@angular/router';
import {ProjectsService} from '@backend';
import {LandingServiceConfigService} from '@services/config/landing-service-config.service';
import {LandingServiceBrokerService} from '@services/landing-service-broker/landing-service-broker.service';
import {UserService} from '@services/user/user.service';
import {lastValueFrom} from 'rxjs';

export interface ProjectAddPageData {
  projectsService: ProjectsService;
}

/**
 * Resolver to fetch the ProjectsService for the current user's organization.
 * This aligns ProjectAddComponent with the resolver-based pattern used in ProjectDetailsComponent.
 */
export const projectAddResolver: ResolveFn<ProjectAddPageData | undefined> = async () => {
  if (isPlatformServer(inject(PLATFORM_ID))) return undefined;

  const landingServiceBroker = inject(LandingServiceBrokerService);
  const landingServiceConfig = inject(LandingServiceConfigService);
  const feUserService = inject(UserService);

  try {
    const user = await feUserService.userPromise;
    if (!user?.organization?.name) {
      return undefined;
    }

    const serviceInstanceMap = await landingServiceBroker.getByOrganizationName(user.organization.name);
    if (!serviceInstanceMap) {
      return undefined;
    }

    const serviceConfiguration = await landingServiceConfig.getPromise(user.organization.name);
    if (serviceConfiguration.project_creation_admin_only) {
      const lsUser = await lastValueFrom(serviceInstanceMap.UsersService.getUser('me'));
      if (!lsUser.is_admin) {
        throw new Error('Project creation is limited to Private Data Enclave administrators only and the user is not an admin.');
      }
    }

    return {
      projectsService: serviceInstanceMap.ProjectsService,
    };
  } catch (err) {
    console.error('Error in projectAddResolver:', err);
    throw err;
  }
};
